import { useQuery } from '@tanstack/react-query';

import { DigitalVisitReasonEnum, ValidatePatientResponseWithScheduledAppointments } from '@src/lib/__generated/api';
import { formatDateForValidation } from '@src/utils/date-parser';
import restfulClient from '@src/utils/restfulClient';

interface validateAccountParams {
	firstName: string;
	lastName: string;
	dateOfBirth: Date | null;
	phoneNumber: string;
	facilityCode: string;
	digitalVisitReason?: DigitalVisitReasonEnum | '';
}

export async function validateAccount({
	firstName,
	lastName,
	dateOfBirth,
	phoneNumber,
	facilityCode,
	digitalVisitReason,
}: validateAccountParams) {
	// NOTE: "v2" here is NOT related to the versioning of this FE application, this is for web API
	const route = 'v2/account/validate';

	const parameters = [
		`FirstName=${encodeURIComponent(firstName)}`,
		`LastName=${encodeURIComponent(lastName)}`,
		`DateOfBirth=${encodeURIComponent(formatDateForValidation(dateOfBirth))}`,
		`PhoneNumber=${encodeURIComponent(phoneNumber.replaceAll(/[^\d]/g, ''))}`,
		`FacilityCode=${encodeURIComponent(facilityCode)}`,
	];

	if (digitalVisitReason) {
		parameters.push(`DigitalVisitReason=${encodeURIComponent(digitalVisitReason)}`);
	}

	const urlParams = parameters.join('&');

	const result: ValidatePatientResponseWithScheduledAppointments = await restfulClient.get({
		route: `${route}?${urlParams}`,
	});

	return result;
}

export function useValidateAccount({
	firstName,
	lastName,
	dateOfBirth,
	phoneNumber,
	facilityCode,
	digitalVisitReason,
}: validateAccountParams) {
	const { error, isLoading, data, refetch } = useQuery<ValidatePatientResponseWithScheduledAppointments, Error>({
		queryKey: ['validateAccount'],
		queryFn: () =>
			validateAccount({
				firstName,
				lastName,
				dateOfBirth,
				phoneNumber,
				facilityCode,
				digitalVisitReason,
			}),
		enabled: false,
	});

	return { error, isLoading, data, refetch };
}
